// komponent tabelki

import React, { Key, ReactElement, ReactNode, useMemo, useEffect } from 'react';
import classnames from 'classnames';
import {
  useMaterialReactTable,
  type MRT_ColumnDef,
  MaterialReactTable
} from 'material-react-table';

import { useRWD } from 'hooks';
import { Pagination } from 'components/controls';

import styles from 'theme/components/controls/Table/Table.module.scss';

export interface ISorter<T> {
  by: keyof T;
  direction: 'asc' | 'desc';
}

export interface IColumn<T> {
  title: ReactNode;
  key?: string;
  dataIndex?: keyof T;
  renderCell?: (record: T, index: number) => ReactNode;
  sortBy?: string;
  width?: number;
  align?: 'left' | 'right' | 'center';
  isMobileHidden?: boolean;
}

// typ danych wejściowych
interface IProps<T> {
  columns: IColumn<T>[];
  dataSource: T[];
  mobileItem?: (record: T, index: number) => ReactNode;
  expandedRowContents?: {
    id: number | string;
    content: ReactNode;
  }[];
  rowKey: keyof T;
  onSortChange?: (sorter: ISorter<T>) => void;
  sorter?: ISorter<T>;
  pagination?: {
    page: number;
    pagesCount: number;
    onChange: (page: number) => void;
  };
  dragableRows?: boolean;
  handleReorder?: (arg: any) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Table = <T extends Record<string, any>>({
  columns,
  rowKey,
  dataSource,
  onSortChange,
  sorter,
  pagination,
  mobileItem,
  expandedRowContents,
  dragableRows,
  handleReorder
}: IProps<T>): ReactElement => {
  const { isMobile } = useRWD();

  const mrtColumns = useMemo<MRT_ColumnDef<T>[]>(() => {
    return columns.map((col) => ({
      id: (col.key || col.dataIndex) as string,
      accessorKey: col.dataIndex as string,
      header: col.title as string,
      enableSorting: !!col.sortBy,
      size: col.width,
      muiTableHeadCellProps: {
        align: col.align
      },
      muiTableBodyCellProps: {
        align: col.align
      },
      Cell: col.renderCell
        ? ({ row }) => {
            if (!row?.original) return null;
            return col.renderCell?.(row.original, row.index);
          }
        : undefined
    }));
  }, [columns]);

  // usunięcie atrybutu style
  useEffect(() => {
    const table = document.querySelector('.react-table');
    table?.removeAttribute('style');
  }, []);

  const table = useMaterialReactTable<T>({
    autoResetPageIndex: false,
    columns: mrtColumns,
    data: dataSource,
    // upewniamy się, że row.id będzie równe wartości row[rowKey] (jako string)
    getRowId: (row) => String(row[rowKey]),
    enableRowOrdering: dragableRows,
    enableSorting: true,
    manualSorting: true,
    enableTopToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableBottomToolbar: false,
    enableColumnResizing: false,
    enableColumnOrdering: false,
    enableExpandAll: false,
    enableExpanding: !!expandedRowContents,
    manualPagination: true, // ważne: mówimy MRT, że backend/parent zarządza stronicowaniem
    rowCount: dataSource.length,
    muiTableProps: {
      className: classnames('react-table', styles.table)
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'unset'
      }
    },
    muiTableHeadCellProps: {
      sx: {
        padding: '8px',
        background: '#F3F4F7',
        fontSize: '12px',
        fontWeight: '400',
        lineHeight: 'unset'
      }
    },
    muiTableBodyCellProps: {
      sx: {
        padding: '16px 8px',
        fontSize: '12px',
        border: 'unset'
      }
    },
    onSortingChange: (updater) => {
      const next =
        typeof updater === 'function'
          ? updater([{ id: sorter?.by as string, desc: sorter?.direction === 'desc' }])
          : updater;
      const nextSort = next?.[0];
      if (nextSort) {
        onHeaderCellClick(nextSort.id);
      }
    },
    muiRowDragHandleProps: ({ table }) => ({
      onDragEnd: () => {
        const { draggingRow, hoveredRow } = table.getState();
        if (draggingRow?.index === hoveredRow?.index) {
          return;
        }

        const isDraggingUp = (draggingRow?.index || 0) > (hoveredRow?.index || 0);
        const entityId = draggingRow?.original.id;
        const beforeEntityId =
          hoveredRow?.index === 0
            ? null
            : isDraggingUp
            ? dataSource[(hoveredRow?.index || 0) - 1].id
            : hoveredRow?.original?.id;
        const afterEntityId =
          hoveredRow?.index === dataSource.length - 1
            ? null
            : isDraggingUp
            ? hoveredRow?.original?.id
            : dataSource[(hoveredRow?.index || 0) + 1].id;
        handleReorder?.({
          entityId,
          beforeEntityId,
          afterEntityId
        });
      }
    }),
    renderEmptyRowsFallback: () => {
      return <div></div>;
    },
    ...(expandedRowContents
      ? {
          renderDetailPanel: ({ row }) => {
            const record = row.original;
            const expanded = expandedRowContents?.find((r) => r.id === record[rowKey]);
            return expanded ? <div>{expanded.content}</div> : null;
          },
          // budujemy stan expanded z kluczami zgodnymi z getRowId (stringi)
          state: {
            expanded: useMemo(() => {
              const allExpanded = (expandedRowContents || []).reduce((acc, r) => {
                acc[String(r.id)] = true;
                return acc;
              }, {} as Record<string, boolean>);
              return allExpanded;
            }, [expandedRowContents])
          }
        }
      : {}),
    displayColumnDefOptions: {
      'mrt-row-expand': {
        size: 0,
        maxSize: 0,
        minSize: 0,
        header: '',
        Cell: () => null
      }
    }
  });

  // funkcja obsługująca zmianę sortoweania
  const onHeaderCellClick = (fieldName: string) => {
    onSortChange?.({
      by: fieldName,
      direction: sorter?.by === fieldName && sorter.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  return (
    <>
      {isMobile ? (
        <div className={styles.mobileList}>
          {dataSource.map((item, rowIndex) =>
            isMobile && mobileItem ? (
              mobileItem(item, rowIndex)
            ) : (
              <div className={styles.mobileListItem} key={item[rowKey]}>
                {columns
                  .filter((column) => !column.isMobileHidden)
                  .map((column) => (
                    <div key={(column.dataIndex || column.key) as Key} className={styles.line}>
                      {column.title ? <div className={styles.label}>{column.title}</div> : null}
                      <div>
                        {column.renderCell
                          ? column.renderCell(item, rowIndex)
                          : column.dataIndex && item[column.dataIndex]}
                      </div>
                    </div>
                  ))}
              </div>
            )
          )}
        </div>
      ) : (
        <>
          <MaterialReactTable table={table} />

          {/* <table className={styles.table}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    className={classnames(styles.th, {
                      [styles['text-align-center']]: !column.align,
                      [styles['cursor-pointer']]: !!column.sortBy
                    })}
                    style={{
                      textAlign: column.align,
                      cursor: 'inherit',
                      width: `${column.width}px`
                    }}
                    key={(column.dataIndex || column.key) as Key}
                    onClick={() => {
                      typeof column.dataIndex !== 'undefined' &&
                        !!column.sortBy &&
                        onHeaderCellClick(column.sortBy);
                    }}
                    align={column.align}>
                    <>
                      {!!column.sortBy && (
                        <span className={styles.sortWrapper}>
                          <ArrowLongThinIcon
                            className={classnames(styles.sortArrow, {
                              [styles.current]:
                                sorter?.by === column.dataIndex && sorter?.direction === 'desc'
                            })}
                          />
                          <ArrowLongThinIcon
                            className={classnames(styles.sortArrow, styles.down, {
                              [styles.current]:
                                sorter?.by === column.dataIndex && sorter?.direction === 'asc'
                            })}
                          />
                        </span>
                      )}
                      <span>{column.title}</span>
                    </>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataSource.map((item, rowIndex) => {
                const expandedContent = expandedRowContents?.find(
                  (expandedRowData) => expandedRowData.id === item[rowKey]
                );

                console.log('expandedContent', expandedContent);
                return (
                  <React.Fragment key={item[rowKey]}>
                    <tr>
                      {columns.map((column) => {
                        return (
                          <td
                            className={styles.td}
                            key={(column.dataIndex || column.key) as Key}
                            align={column.align}>
                            <>
                              {column.renderCell
                                ? column.renderCell(item, rowIndex)
                                : column.dataIndex && item[column.dataIndex]}
                            </>
                          </td>
                        );
                      })}
                    </tr>
                    {!!expandedContent && (
                      <tr className="expanded-row">
                        <td colSpan={columns.length}>{expandedContent.content}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table> */}
        </>
      )}

      {pagination && (
        <Pagination
          pagesCount={pagination.pagesCount}
          page={pagination.page}
          onChange={pagination.onChange}
        />
      )}
    </>
  );
};

export default Table;
